import { useEffect, useRef, useState, type CSSProperties, type JSX } from 'react';
import {
  AlertTriangleIcon,
  Badge,
  Button,
  CheckIcon,
  ConfidenceBadge,
  Field,
  Input,
  Logo,
  ProvenanceBadge,
  PublicationCard,
  ScriptPreview,
  Stepper
} from '@viniciomendesr/rayrovisk-design-system';
import { ApiError } from '../api/client';
import { instagramHandle, isInstagramHandle } from '../api/handle';
import { thumbnailSrc, type ProfileHalf, type ScriptHalf } from '../api/profileReading';
import { readProfileFrames } from '../api/readingFrames';
import {
  compactCount,
  formatName,
  metricLabel,
  missingMetricNote,
  monthAndDay,
  windowSentence
} from './profileReadingCopy';
import { ReadingOffer } from './ReadingOffer';

/**
 * What the reading is actually doing, in the order it does it.
 *
 * The design's third line read "Separando a que mais gente salvou". Saves are
 * not public — the endpoint carries likes, comments and, on video, views — so
 * that line described work that never happened. Its second line said "dos
 * últimos 90 dias", which the endpoint does not answer either: it returns the
 * most recent posts, and how far back those reach depends on how often she
 * posts. The result prints the real span.
 */
const STEPS = [
  'Abrindo o perfil público',
  'Contando as publicações mais recentes',
  'Somando curtidas e comentários de cada uma',
  'Montando o roteiro que repete o acerto'
] as const;

/**
 * How many of the four lines the `profile` frame answers. Opening the profile,
 * reading the posts and summing them are all settled by the time it lands, at
 * about one second; the fourth waits on the model, some fourteen seconds later.
 */
const PROFILE_STEPS = 3;

/** Four lines are a list. The stagger stops here so it stays one. */
const RISE_DELAY = ['', 'rvk-rise-2', 'rvk-rise-3', 'rvk-rise-4'] as const;

/**
 * The page's own section label. It is a heading everywhere it appears here —
 * "Por que ele foi bem" names the panel under it, and the reading step names
 * the handle — so it is written as one rather than as decoration above one.
 */
const sectionLabel: CSSProperties = {
  margin: 0,
  font: '600 9.5px/1 var(--rvk-font-functional)',
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--rvk-text-tertiary)'
};

const body: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  padding: '26px clamp(18px, 4vw, 32px) 28px'
};

const title: CSSProperties = {
  margin: 0,
  font: '400 clamp(23px, 2.8vw, 31px)/1.15 var(--rvk-font-expressive)',
  letterSpacing: '-.02em',
  color: 'var(--rvk-text-primary)',
  textWrap: 'balance'
};

const note: CSSProperties = {
  font: '400 12px/1.5 var(--rvk-font-functional)',
  color: 'var(--rvk-text-tertiary)'
};

type Stage = 'form' | 'reading';

export interface ProfileReadingDialogProps {
  onClose: () => void;
  /** Reported so the funnel is measurable without this dialog knowing PostHog. */
  onEvent?: (name: string, properties?: Record<string, unknown>) => void;
  /** Threaded to `ReadingOffer`, the only place an address is taken. */
  onEmailCaptured?: (email: string) => void;
}

export function ProfileReadingDialog({
  onClose,
  onEvent,
  onEmailCaptured
}: ProfileReadingDialogProps): JSX.Element {
  const [stage, setStage] = useState<Stage>('form');
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string>();
  const [profile, setProfile] = useState<ProfileHalf>();
  const [script, setScript] = useState<ScriptHalf>();
  const [frameError, setFrameError] = useState<string>();
  const dialogRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<AbortController>();

  /**
   * Focus starts on the field she came here to fill, not on the way out.
   *
   * The close button is still the first thing Tab reaches, because it is first
   * in the DOM — which is the arrangement that lets someone leave immediately
   * without hunting for it.
   */
  useEffect(() => {
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, []);

  /** The page behind a modal must not scroll under it. */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Leaving has to stop the reading. The model is still generating on the other
  // end of this stream, and it is billed by the token whether or not anyone is
  // left to read it.
  useEffect(() => () => streamRef.current?.abort(), []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      // Without this, Tab walks out of an open modal and into the sales page
      // underneath, where the keyboard user is operating a screen they cannot
      // see and cannot get back from.
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea, select, iframe, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  async function submit(): Promise<void> {
    const handle = instagramHandle(typed);
    if (!handle || !isInstagramHandle(typed)) {
      setError('Escreva o seu @ para o Roteirovisk ler o perfil.');
      return;
    }

    setError(undefined);
    setProfile(undefined);
    setScript(undefined);
    setFrameError(undefined);
    setStage('reading');
    // Never the handle itself. It names a real person's account, it is the one
    // thing she typed, and no funnel question needs it — the counts and the
    // failure codes answer all of them.
    onEvent?.('profile_reading_requested');
    const startedAt = Date.now();

    streamRef.current?.abort();
    const controller = new AbortController();
    streamRef.current = controller;

    // Read rather than derived from `profile`: this closure was created before
    // the first frame landed and never sees the state it set.
    let half: ProfileHalf | undefined;

    try {
      for await (const frame of readProfileFrames(handle.slice(1), {
        signal: controller.signal
      })) {
        if (frame.frame === 'profile') {
          half = frame;
          setProfile(frame);
          // Its own name, not `delivered` with a new meaning. This is the wait
          // the split was made to shorten, and it is a different question from
          // whether the roteiro ever arrived.
          onEvent?.('profile_reading_previewed', {
            confidence: frame.confidence.level,
            posts_read: frame.window.postsRead,
            format: frame.topPost.format,
            duration_ms: Date.now() - startedAt
          });
        }
        if (frame.frame === 'script') {
          setScript(frame);
          // Still "the whole reading is on screen", so this number stays
          // comparable with every one recorded before the response was split.
          onEvent?.('profile_reading_delivered', {
            confidence: half?.confidence.level,
            posts_read: half?.window.postsRead,
            format: half?.topPost.format,
            duration_ms: Date.now() - startedAt
          });
        }
        if (frame.frame === 'error') {
          setFrameError(frame.message);
          onEvent?.('profile_reading_failed', { code: frame.code });
        }
      }
    } catch (cause) {
      if (controller.signal.aborted) {
        return;
      }

      // The service writes a sentence meant for the person reading it: "esse
      // perfil é fechado" is a different problem from "não achei esse perfil",
      // and each has its own next step.
      const message = cause instanceof ApiError ? cause.message : undefined;
      if (half) {
        setFrameError(message ?? 'Não consegui montar o roteiro agora.');
      } else {
        setError(message ?? 'Não consegui ler agora. Tente de novo em alguns minutos.');
        setStage('form');
      }
      onEvent?.('profile_reading_failed', {
        code: cause instanceof ApiError ? cause.code : 'unknown'
      });
    }
  }

  return (
    <div className="rvk-reading-backdrop" role="presentation">
      <div
        ref={dialogRef}
        className="rvk-reading-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reading-title"
      >
        <div
          className="rvk-reading-topbar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '13px 16px 12px',
            borderBottom: '1px solid var(--rvk-border-subtle)'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
            <Logo variant="symbol" size={26} />
            <span style={sectionLabel}>Análise gratuita do seu perfil</span>
          </span>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div style={{ padding: '12px clamp(18px, 4vw, 32px) 0' }}>
          <Stepper
            label="Leitura do perfil"
            labelHidden
            steps={[{ label: 'Seu perfil' }, { label: 'Leitura' }, { label: 'Seu roteiro' }]}
            current={stage === 'form' ? 0 : profile ? 2 : 1}
            orientation="horizontal"
          />
        </div>

        {stage === 'form' ? (
          <FormStage typed={typed} error={error} onChange={setTyped} onSubmit={submit} />
        ) : (
          <ReadingStage
            handle={instagramHandle(typed)}
            profile={profile}
            script={script}
            frameError={frameError}
            onEvent={onEvent}
            onEmailCaptured={onEmailCaptured}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function FormStage({
  typed,
  error,
  onChange,
  onSubmit
}: {
  typed: string;
  error: string | undefined;
  onChange: (value: string) => void;
  onSubmit: () => void;
}): JSX.Element {
  return (
    <form
      style={body}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {/*
        Not "nos últimos 90 dias", which the reading cannot promise: the public
        endpoint answers with the most recent posts, and how far back those
        reach depends on how often she posts. The result says the real span.
      */}
      <h2 id="reading-title" style={title}>
        Receba uma análise do seu melhor post
      </h2>
      <p
        style={{
          margin: 0,
          font: '400 15px/1.6 var(--rvk-font-functional)',
          color: 'var(--rvk-text-secondary)',
          maxWidth: '52ch',
          textWrap: 'pretty'
        }}
      >
        Você sai daqui com o roteiro do próximo post, tirado do que já funcionou no seu perfil.
      </p>

      <div style={{ maxWidth: 340 }}>
        <Field label="Seu @ no Instagram" hint="Sem senha e sem login." error={error}>
          <Input
            size="lg"
            placeholder="@seu_perfil"
            value={typed}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(event) => onChange(event.target.value)}
          />
        </Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <Button type="submit" variant="primary" size="lg">
          Analisar meu melhor post
        </Button>
      </div>
      {/* Not "no máximo 20 segundos": the model call's own ceiling is 25, so
          that sentence promised something the timeout could break. Measured
          median on a real profile is under four. */}
      <span style={note}>Leva alguns segundos</span>
    </form>
  );
}

/**
 * One screen for the whole stream.
 *
 * The reading and the result were two stages while one request answered once.
 * They are the same stage now: the profile arrives at about a second and the
 * roteiro some fourteen seconds after it, and the screen between those two
 * moments is a real state someone sits in, not a transition.
 */
function ReadingStage({
  handle,
  profile,
  script,
  frameError,
  onEvent,
  onEmailCaptured,
  onClose
}: {
  handle: string | undefined;
  profile: ProfileHalf | undefined;
  script: ScriptHalf | undefined;
  frameError: string | undefined;
  onEvent?: (name: string, properties?: Record<string, unknown>) => void;
  onEmailCaptured?: (email: string) => void;
  onClose: () => void;
}): JSX.Element {
  const settled = script !== undefined || frameError !== undefined;

  return (
    <div
      // The card lands around a second in, the roteiro some fourteen seconds
      // after it, with nothing announced between them for a screen reader.
      // `polite` waits for a pause instead of interrupting whatever else is
      // being read.
      aria-live="polite"
      style={{
        ...body,
        gap: profile ? 22 : 18,
        padding: profile
          ? '24px clamp(18px, 4vw, 32px) 28px'
          : '44px clamp(18px, 4vw, 32px) 52px'
      }}
    >
      {profile ? <FoundHeader profile={profile} /> : <LookingHeader handle={handle} />}

      {profile ? <TopPost profile={profile} /> : null}

      {/*
        Whatever is happening to the roteiro happens here, under the card. A
        wait that sits above the card would push it down the moment the roteiro
        landed, and the card moving at second fifteen is the one thing this
        screen cannot afford.
      */}
      {script && profile ? (
        <ScriptSection profile={profile} script={script} />
      ) : frameError ? (
        <ScriptFailure message={frameError} />
      ) : (
        <ReadingProgress done={profile ? PROFILE_STEPS : 0} />
      )}

      {profile && settled && profile.offerAvailable ? (
        <ReadingOffer
          handle={profile.handle}
          remainingScenes={script?.script.remaining}
          onEvent={onEvent}
          onEmailCaptured={onEmailCaptured}
          onClose={onClose}
        />
      ) : null}
    </div>
  );
}

function LookingHeader({ handle }: { handle: string | undefined }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h2 id="reading-title" style={sectionLabel}>
        Lendo o perfil
      </h2>
      <span
        style={{
          font: '400 clamp(21px, 2.4vw, 25px)/1.25 var(--rvk-font-expressive)',
          color: 'var(--rvk-text-primary)'
        }}
      >
        {handle ?? '@seu_perfil'}
      </span>
    </div>
  );
}

function FoundHeader({ profile }: { profile: ProfileHalf }): JSX.Element {
  return (
    <div className="rvk-rise" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{ font: '500 14px/1 var(--rvk-font-functional)', color: 'var(--rvk-text-primary)' }}
        >
          @{profile.handle}
        </span>
        {/*
          Deliberately not a ProvenanceBadge: that component's `official`
          means observed in Meta Graph, and this was read off the public
          profile page. The badge further down marks the part that really is
          an interpretation.
        */}
        <span style={note}>O que é público no perfil</span>
      </div>
      <h2 id="reading-title" style={title}>
        Este foi o seu post com mais engajamento
      </h2>
      <p
        style={{
          margin: 0,
          font: '400 13.5px/1.5 var(--rvk-font-functional)',
          color: 'var(--rvk-text-secondary)'
        }}
      >
        {windowSentence(profile)}
      </p>
    </div>
  );
}

function TopPost({ profile }: { profile: ProfileHalf }): JSX.Element {
  const { topPost } = profile;
  const engagement = topPost.metrics.find((metric) => metric.kind === 'engagement')?.value;

  // The card's metric vocabulary has no "engagement", and its `saves` slot is a
  // bookmark icon over a number Instagram does not publish. The total goes
  // beside the card as a tag rather than borrowing an icon that would lie.
  const cardMetrics = topPost.metrics.flatMap((metric) =>
    metric.kind === 'engagement' || metric.value === null
      ? []
      : [{ kind: metric.kind, formatted: compactCount(metric.value) }]
  );

  const withheld = missingMetricNote(topPost.metrics);

  return (
    <div
      className="rvk-rise rvk-rise-2"
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <PublicationCard
        format={topPost.format}
        orientation="horizontal"
        rank={1}
        thumbnailUrl={topPost.thumbnailUrl ? thumbnailSrc(topPost.thumbnailUrl) : undefined}
        caption={topPost.caption || `${formatName(topPost.format)} sem legenda`}
        publishedAt={monthAndDay(topPost.publishedAt)}
        metrics={cardMetrics}
        tags={
          engagement == null ? undefined : (
            <Badge tone="accent" variant="subtle" size="sm" numeric>
              {compactCount(engagement)} {metricLabel('engagement')}
            </Badge>
          )
        }
      />
      {withheld ? <p style={{ ...note, margin: 0 }}>{withheld}</p> : null}
    </div>
  );
}

/**
 * The four lines, completing as frames land rather than on a timer.
 *
 * All four are on screen from the first render so the block keeps its height
 * while they resolve. Only the one being worked on pulses: three dots beating
 * at once is a screen with nothing to look at.
 *
 * The lines say what was done and never what it came to. The engagement total
 * belongs to the card above, and the same figure printed twice a hand's width
 * apart teaches the reader that one of the two is noise.
 */
function ReadingProgress({ done }: { done: number }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={STEPS.length}
        aria-valuenow={done}
        aria-label="Leitura do perfil"
        style={{
          height: 6,
          borderRadius: 999,
          // Sunken rather than surface-2, which is within a percent of the
          // dialog's cream: an empty track has to read as a track and not as a
          // rule under the handle.
          background: 'var(--rvk-surface-sunken)',
          overflow: 'hidden'
        }}
      >
        <div
          className="rvk-reading-meter"
          style={{
            height: '100%',
            width: '100%',
            borderRadius: 999,
            background: 'var(--rvk-accent-default)',
            transformOrigin: 'left',
            transform: `scaleX(${done / STEPS.length})`
          }}
        />
      </div>

      <ol
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 11
        }}
      >
        {STEPS.map((line, index) => {
          const complete = index < done;
          return (
            <li
              key={line}
              className={['rvk-reading-step', 'rvk-rise', RISE_DELAY[index]].join(' ').trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                font: '400 14px/1.5 var(--rvk-font-functional)',
                color: complete
                  ? 'var(--rvk-text-primary)'
                  : index === done
                    ? 'var(--rvk-text-secondary)'
                    : 'var(--rvk-text-tertiary)'
              }}
            >
              <span
                style={{
                  flex: 'none',
                  display: 'inline-flex',
                  width: 16,
                  justifyContent: 'center'
                }}
              >
                {complete ? (
                  <CheckIcon size={15} />
                ) : (
                  <span className={index === done ? 'rvk-reading-pulse' : 'rvk-reading-dot'} />
                )}
              </span>
              {line}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * The roteiro's slot when the roteiro is what failed.
 *
 * The service writes the sentence and this prints it alone. `ErrorState` would
 * be the component for this and carries a default second line — "não
 * conseguimos buscar estes dados agora" — which is the system talking about
 * itself over the top of the one sentence that knows what actually broke.
 */
function ScriptFailure({ message }: { message: string }): JSX.Element {
  return (
    <div
      className="rvk-rise"
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '15px 17px',
        background: 'var(--rvk-feedback-error-bg)',
        border: '1px solid var(--rvk-feedback-error-border)',
        borderRadius: 'var(--rvk-radius-md)',
        font: '400 14px/1.5 var(--rvk-font-functional)',
        color: 'var(--rvk-feedback-error-fg)'
      }}
    >
      <span style={{ flex: 'none', display: 'inline-flex' }} aria-hidden="true">
        <AlertTriangleIcon size={18} />
      </span>
      {message}
    </div>
  );
}

function ScriptSection({
  profile,
  script
}: {
  profile: ProfileHalf;
  script: ScriptHalf;
}): JSX.Element {
  return (
    <div className="rvk-rise" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          padding: '16px 18px',
          background: 'var(--rvk-surface-1)',
          border: '1px solid var(--rvk-border-subtle)',
          borderRadius: 'var(--rvk-radius-md)'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={sectionLabel}>Por que ele foi bem</h3>
          {/* Reading the numbers is an interpretation. The numbers are not. */}
          <ProvenanceBadge kind="inference">Leitura da IA</ProvenanceBadge>
          <ConfidenceBadge level={profile.confidence.level} />
        </span>
        {script.whyItWorked.map((line) => (
          <span
            key={line}
            style={{
              display: 'flex',
              gap: 8,
              font: '400 13.5px/1.55 var(--rvk-font-functional)',
              color: 'var(--rvk-text-primary)'
            }}
          >
            <span style={{ color: 'var(--rvk-accent-default)', flex: 'none' }}>
              <CheckIcon size={15} />
            </span>
            {line}
          </span>
        ))}
      </section>

      <p
        style={{
          margin: 0,
          font: '400 15px/1.55 var(--rvk-font-functional)',
          color: 'var(--rvk-text-primary)',
          maxWidth: '54ch',
          textWrap: 'pretty'
        }}
      >
        Você já fez um post funcionar. O que cansa é acertar de novo na semana seguinte.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h3 style={sectionLabel}>Repita o que deu certo no próximo Reel</h3>
        <ScriptPreview
          label={script.script.label}
          cards={script.script.scenes}
          remaining={script.script.remaining}
          remainingLabel={`mais ${script.script.remaining} cenas e a legenda`}
        />
      </div>
    </div>
  );
}
